import { AlertCircle, Check, Trash2, Truck, Upload } from "lucide-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

import ConfirmDialog from "@/components/common/ConfirmDialog";
import HotToast from "@/components/common/HotToast";
import businessInfoService from "@/services/businessInfoService";
import documentsService from "@/services/documentsService";

interface FleetApplicationCardProps {
  isSubmitting?: boolean;
  onDocumentUploaded?: () => void;
  onSubmit?: (data: any) => void;
  showDocuments?: boolean;
}

const FleetApplicationCard: React.FC<FleetApplicationCardProps> = ({
  isSubmitting = false,
  onDocumentUploaded,
  onSubmit,
  showDocuments = true,
}) => {
  const dayNames = {
    friday: "Friday",
    monday: "Monday",
    saturday: "Saturday",
    sunday: "Sunday",
    thursday: "Thursday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    address: "",
    businessType: "",
    company_name: "",
    description: "",
    documents: {
      businessLicense: null as File | null,
      fleetManifest: null as File | null,
      insuranceCertificate: null as File | null,
      operatingPermit: null as File | null,
      taxCertificate: null as File | null,
    },
    email: "",
    fleet_size: "",

    operating_area: "",
    operatingHours: {
      friday: { close: "23:00", isOpen: true, open: "09:00" },
      monday: { close: "22:00", isOpen: true, open: "09:00" },
      saturday: { close: "23:00", isOpen: true, open: "08:00" },
      sunday: { close: "22:00", isOpen: true, open: "08:00" },
      thursday: { close: "22:00", isOpen: true, open: "09:00" },
      tuesday: { close: "22:00", isOpen: true, open: "09:00" },
      wednesday: { close: "22:00", isOpen: true, open: "09:00" },
    },
    phone: "",
    services: {
      delivery: false,
      logistics: false,
      moving: false,
      rideshare: false,
    },
    vehicle_types: {
      bicycles: false,
      cars: false,
      motorcycles: false,
      trucks: false,
      vans: false,
    },
    website: "",
  });

  // Load business info on mount
  useEffect(() => {
    if (!showDocuments) {
      businessInfoService.getBusinessInfo().then((info) => {
        console.log("BusinessInfoService response:", info);
        // Normalize backend operating_hours to frontend operatingHours
        const apiHours = (info.operating_hours || {}) as any;
        const defaultHours = {
          friday: { close: "", isOpen: false, open: "" },
          monday: { close: "", isOpen: false, open: "" },
          saturday: { close: "", isOpen: false, open: "" },
          sunday: { close: "", isOpen: false, open: "" },
          thursday: { close: "", isOpen: false, open: "" },
          tuesday: { close: "", isOpen: false, open: "" },
          wednesday: { close: "", isOpen: false, open: "" },
        };
        const mappedHours = Object.keys(defaultHours).reduce(
          (acc, day) => {
            const dayData = apiHours[day] || {};
            acc[day as keyof typeof defaultHours] = {
              close: dayData.close ?? "",
              isOpen: Boolean(dayData.isOpen),
              open: dayData.open ?? "",
            };
            return acc;
          },
          {} as typeof defaultHours,
        );

        // Normalize business_type, fleet_size, vehicle_types, and services
        const mappedInfo = {
          ...info,
          businessType: info.business_type || "",
          fleetSize: info.fleet_size || "",
          services: info.services || {
            delivery: false,
            logistics: false,
            moving: false,
            rideshare: false,
          },
          vehicle_types: info.vehicle_types || {
            bicycles: false,
            cars: false,
            motorcycles: false,
            trucks: false,
            vans: false,
          },
        };
        setFormData((prev) => ({
          ...prev,
          ...mappedInfo,
          operatingHours: mappedHours,
        }));
      });
    }
  }, [showDocuments]);

  const [uploadedDocuments, setUploadedDocuments] = useState<{
    [key: string]: boolean;
  }>({});
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [replacingDoc, setReplacingDoc] = useState<null | string>(null);

  // Fetch uploaded documents on mount if in documents mode
  useEffect(() => {
    if (showDocuments) {
      setLoadingDocuments(true);
      documentsService.getUserDocuments().then((response: any) => {
        const uploadedState: { [key: string]: boolean } = {};
        if (response.documents && Array.isArray(response.documents)) {
          response.documents.forEach((doc: any) => {
            // Map backend type to frontend key if needed
            let key = doc.document_type || doc.type;
            // Map backend keys to frontend keys for this card
            if (key === "business_license") key = "businessLicense";
            if (key === "fleet_manifest") key = "fleetManifest";
            if (key === "insurance") key = "insuranceCertificate";
            if (key === "operating_permit") key = "operatingPermit";
            if (key === "tax_document") key = "taxCertificate";
            uploadedState[key] = true;
          });
        }
        setUploadedDocuments(uploadedState);
        setLoadingDocuments(false);
      });
    }
  }, [showDocuments]);
  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCheckboxChange = (
    category: "services" | "vehicle_types",
    field: string,
    value: boolean,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value,
      },
    }));
  };

  const handleHoursChange = (
    day: keyof typeof formData.operatingHours,
    field: "close" | "isOpen" | "open",
    value: boolean | string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        [day]: {
          ...prev.operatingHours[day],
          [field]: value,
        },
      },
    }));
  };

  const handleFileUpload = async (documentType: string, file: File) => {
    if (showDocuments) {
      // Map all frontend keys to backend types
      const docTypeMap: { [key: string]: string } = {
        businessLicense: "business_license",
        fleetManifest: "fleet_manifest",
        insuranceCertificate: "insurance",
        operatingPermit: "operating_permit",
        taxCertificate: "tax_document",
      };
      const backendType = docTypeMap[documentType] || documentType;
      try {
        await documentsService.uploadDocument(backendType, file);
        setUploadedDocuments((prev) => ({ ...prev, [documentType]: true }));
        toast.success("Document uploaded successfully.");
        if (typeof onDocumentUploaded === "function") onDocumentUploaded();
      } catch (e: any) {
        const backendMsg = e?.response?.data?.message;
        if (
          backendMsg &&
          backendMsg.includes("already exists") &&
          backendMsg.includes("replace_existing=true")
        ) {
          toast.error(
            "A document of this type already exists. To replace it, please use the 'Replace' option.",
          );
        } else if (e?.response?.data?.errors) {
          toast.error(
            "Validation error: " + JSON.stringify(e.response.data.errors),
          );
        } else if (backendMsg) {
          toast.error(backendMsg);
        } else {
          toast.error("Failed to upload document. Please try again.");
        }
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        documents: {
          ...prev.documents,
          [documentType]: file,
        },
      }));
      setUploadedDocuments((prev) => ({ ...prev, [documentType]: true }));
    }
  };
  const handleDelete = async () => {
    setDeleting(true);
    try {
      // Simulate delete API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success("Application deleted successfully.");
      setConfirmOpen(false);
    } catch (err) {
      toast.error("Failed to delete application.");
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Prepare payload for backend
    const payload = {
      ...formData,
      operating_hours: (formData as any).operatingHours,
    };
    try {
      await businessInfoService.updateBusinessInfo(payload);
      toast.success("Information updated successfully.");
      if (onSubmit) onSubmit(formData);
    } catch (err: any) {
      const backendMsg = err?.response?.data?.message;
      toast.error(
        backendMsg || err.message || "Failed to update business info.",
      );
    }
  };

  const requiredDocuments = [
    {
      description: "Valid business registration and operating license",
      key: "businessLicense",
      label: "Business License",
      required: true,
    },
    {
      description: "Commercial vehicle insurance coverage proof",
      key: "insuranceCertificate",
      label: "Insurance Certificate",
      required: true,
    },
    {
      description: "Transportation/logistics operating permit",
      key: "operatingPermit",
      label: "Operating Permit",
      required: true,
    },
    {
      description: "Tax registration and compliance certificate",
      key: "taxCertificate",
      label: "Tax Certificate",
      required: true,
    },
    {
      description: "List of vehicles with registration details",
      key: "fleetManifest",
      label: "Fleet Manifest",
      required: false,
    },
  ];

  const vehicleTypeOptions = [
    { description: "Standard passenger vehicles", key: "cars", label: "Cars" },
    { description: "Cargo and delivery vans", key: "vans", label: "Vans" },
    {
      description: "Heavy-duty transport vehicles",
      key: "trucks",
      label: "Trucks",
    },
    {
      description: "Two-wheeler delivery vehicles",
      key: "motorcycles",
      label: "Motorcycles",
    },
    {
      description: "Eco-friendly delivery bikes",
      key: "bicycles",
      label: "Bicycles",
    },
  ];

  const serviceOptions = [
    {
      description: "Package and food delivery",
      key: "delivery",
      label: "Delivery Services",
    },
    {
      description: "Passenger transportation",
      key: "rideshare",
      label: "Rideshare",
    },
    {
      description: "Supply chain and freight",
      key: "logistics",
      label: "Logistics",
    },
    {
      description: "Residential and commercial moving",
      key: "moving",
      label: "Moving Services",
    },
  ];

  return (
    <div className="rounded-lg bg-white p-4 shadow-lg dark:bg-gray-800 sm:p-6 lg:p-8">
      <HotToast />
      <ConfirmDialog
        cancelText="Cancel"
        confirmText={deleting ? "Deleting..." : "Delete"}
        message="Are you sure you want to delete this application? This action cannot be undone."
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        open={confirmOpen}
        title="Delete Application"
      />
      <div className="mb-4 sm:mb-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="rounded-lg bg-blue-100 p-2.5 dark:bg-blue-900/20 sm:p-3">
            <Truck className="h-6 w-6 text-blue-600 dark:text-blue-400 sm:h-8 sm:w-8" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 sm:text-2xl">
              Fleet Partnership Application
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 sm:text-base">
              Join our network as a fleet management partner.
            </p>
          </div>
        </div>
      </div>

      <form className="space-y-6 sm:space-y-8" onSubmit={handleSubmit}>
        {!showDocuments && (
          <>
            {/* Company Information */}
            <div>
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Company Information
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Company Name *
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-base"
                    onChange={(e) =>
                      handleInputChange("companyName", e.target.value)
                    }
                    placeholder="Your Company Name"
                    required
                    type="text"
                    value={formData.company_name || ""}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Fleet Size *
                  </label>
                  <select
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    onChange={(e) =>
                      handleInputChange("fleet_size", e.target.value)
                    }
                    required
                    value={formData.fleet_size || ""}
                  >
                    <option value="">Select Fleet Size</option>
                    <option value="1-5 vehicles">1-5 vehicles</option>
                    <option value="6-20 vehicles">6-20 vehicles</option>
                    <option value="21-50 vehicles">21-50 vehicles</option>
                    <option value="51-100 vehicles">51-100 vehicles</option>
                    <option value="100+ vehicles">100+ vehicles</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Business Type *
                  </label>
                  <select
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    onChange={(e) =>
                      handleInputChange("businessType", e.target.value)
                    }
                    required
                    value={formData.businessType || ""}
                  >
                    <option value="">Select Business Type</option>
                    <option value="Transportation Company">
                      Transportation Company
                    </option>
                    <option value="Logistics Provider">
                      Logistics Provider
                    </option>
                    <option value="Delivery Service">Delivery Service</option>
                    <option value="Rideshare Company">Rideshare Company</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Operating Area *
                  </label>
                  <select
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    onChange={(e) =>
                      handleInputChange("operating_area", e.target.value)
                    }
                    required
                    value={formData.operating_area || ""}
                  >
                    <option value="">Select Operating Area</option>
                    <option value="City Center">City Center</option>
                    <option value="Suburbs">Suburbs</option>
                    <option value="Region A">Region A</option>
                    <option value="Region B">Region B</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Business Address *
                  </label>
                  <textarea
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    onChange={(e) =>
                      handleInputChange("address", e.target.value)
                    }
                    placeholder="Full business address"
                    required
                    rows={3}
                    value={formData.address || ""}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Phone Number *
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    required
                    type="tel"
                    value={formData.phone || ""}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email Address *
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="company@email.com"
                    readOnly
                    required
                    type="email"
                    value={formData.email || ""}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Website
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    onChange={(e) =>
                      handleInputChange("website", e.target.value)
                    }
                    placeholder="https://www.yourcompany.com"
                    type="url"
                    value={formData.website || ""}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Company Description
                  </label>
                  <textarea
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    placeholder="Tell us about your company, experience, and what services you provide..."
                    rows={4}
                    value={formData.description || ""}
                  />
                </div>
              </div>
            </div>
            {/* Operating Hours */}
            <div>
              <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
                Operating Hours
              </h3>
              <div className="space-y-3">
                {Object.entries(formData.operatingHours).map(([day, hours]) => (
                  <div
                    className="flex flex-col gap-2.5 rounded-lg bg-gray-50 p-3 dark:bg-gray-700 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                    key={day}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        checked={hours.isOpen}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        onChange={(e) =>
                          handleHoursChange(
                            day as keyof typeof formData.operatingHours,
                            "isOpen",
                            e.target.checked,
                          )
                        }
                        type="checkbox"
                      />
                      <span className="min-w-[5rem] font-medium capitalize text-gray-900 dark:text-gray-100">
                        {dayNames[day as keyof typeof dayNames]}
                      </span>
                    </div>
                    {hours.isOpen ? (
                      <div className="flex flex-1 items-center gap-1.5 pl-7 sm:flex-none sm:gap-2 sm:pl-0">
                        <input
                          className="min-w-0 flex-1 rounded border border-gray-300 px-1.5 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-600 dark:text-white sm:w-auto sm:flex-none sm:px-2"
                          onChange={(e) =>
                            handleHoursChange(
                              day as keyof typeof formData.operatingHours,
                              "open",
                              e.target.value,
                            )
                          }
                          type="time"
                          value={hours.open || ""}
                        />
                        <span className="flex-shrink-0 text-xs text-gray-500 sm:text-sm">
                          to
                        </span>
                        <input
                          className="min-w-0 flex-1 rounded border border-gray-300 px-1.5 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-600 dark:text-white sm:w-auto sm:flex-none sm:px-2"
                          onChange={(e) =>
                            handleHoursChange(
                              day as keyof typeof formData.operatingHours,
                              "close",
                              e.target.value,
                            )
                          }
                          type="time"
                          value={hours.close || ""}
                        />
                      </div>
                    ) : (
                      <span className="pl-7 text-gray-500 dark:text-gray-400 sm:pl-0">
                        Closed
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            {/* Vehicle Types */}
            <div>
              <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
                Vehicle Types
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {vehicleTypeOptions.map((option) => (
                  <div
                    className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                    key={option.key}
                  >
                    <label className="flex cursor-pointer items-start gap-3">
                      <input
                        checked={
                          formData.vehicle_types[
                            option.key as keyof typeof formData.vehicle_types
                          ]
                        }
                        className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        onChange={(e) =>
                          handleCheckboxChange(
                            "vehicle_types",
                            option.key,
                            e.target.checked,
                          )
                        }
                        type="checkbox"
                      />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {option.label}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {option.description}
                        </div>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
            {/* Services */}
            <div>
              <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
                Services Offered
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {serviceOptions.map((option) => (
                  <div
                    className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                    key={option.key}
                  >
                    <label className="flex cursor-pointer items-start gap-3">
                      <input
                        checked={
                          formData.services[
                            option.key as keyof typeof formData.services
                          ]
                        }
                        className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        onChange={(e) =>
                          handleCheckboxChange(
                            "services",
                            option.key,
                            e.target.checked,
                          )
                        }
                        type="checkbox"
                      />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {option.label}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {option.description}
                        </div>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        {/* required documents */}
        {showDocuments && (
          <div>
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              Required Documents
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {requiredDocuments.map((doc) => (
                <div
                  className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                  key={doc.key}
                >
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                      {doc.label}
                      {doc.required && (
                        <span className="ml-1 text-red-500">*</span>
                      )}
                    </h4>
                    {uploadedDocuments[doc.key] && (
                      <Check className="h-5 w-5 text-green-500" />
                    )}
                  </div>
                  <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                    {doc.description}
                  </p>
                  <label
                    className={`flex items-center justify-center rounded-lg border-2 p-4 ${uploadedDocuments[doc.key] ? "cursor-not-allowed border-green-400 bg-green-50 dark:border-green-600 dark:bg-green-900/20" : "cursor-pointer border-dashed border-gray-300 hover:border-blue-500 dark:border-gray-600 dark:hover:border-blue-400"}`}
                  >
                    {uploadedDocuments[doc.key] ? (
                      <>
                        <div className="mt-2 flex items-center justify-center rounded-lg border border-green-400 bg-green-900/20 p-4 text-green-300 dark:bg-green-900/30">
                          <svg
                            className="mr-2 h-8 w-8 text-green-400"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path
                              d="M9 12l2 2l4 -4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <circle cx="12" cy="12" r="10" />
                          </svg>
                          <span className="text-base font-semibold text-green-200">
                            Document uploaded successfully
                          </span>
                        </div>
                        <div className="mt-2 flex justify-center gap-4">
                          <label className="cursor-pointer text-blue-600 hover:underline">
                            <input
                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                              className="hidden"
                              disabled={loadingDocuments}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleFileUpload(doc.key, file);
                              }}
                              type="file"
                            />
                            Replace
                          </label>
                          <button
                            className="text-red-600 hover:underline"
                            disabled={loadingDocuments}
                            onClick={() => {
                              // Simulate delete for now, you can wire to API
                              setUploadedDocuments((prev) => ({
                                ...prev,
                                [doc.key]: false,
                              }));
                              toast.success("Document deleted");
                            }}
                            type="button"
                          >
                            Delete
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="text-center">
                        <Upload className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Click to upload or drag and drop
                        </p>
                        <input
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          className="hidden"
                          disabled={loadingDocuments}
                          id={`file-upload-${doc.key}`}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileUpload(doc.key, file);
                          }}
                          type="file"
                        />
                        <label
                          className="cursor-pointer text-blue-600 hover:underline"
                          htmlFor={`file-upload-${doc.key}`}
                        >
                          Upload
                        </label>
                      </div>
                    )}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Submit Button (hidden in business documents tab) */}
        {!showDocuments && (
          <div className="flex justify-end">
            <button
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-white hover:bg-blue-700 disabled:opacity-50"
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        )}
      </form>

      {/* Information Box */}
      <div className="mt-6 rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-400">
              Partnership Benefits
            </h4>
            <p className="mt-1 text-sm text-blue-800 dark:text-blue-300">
              As a fleet partner, you'll get access to our booking platform,
              real-time tracking, automated billing, and 24/7 support. We handle
              customer acquisition while you focus on operations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FleetApplicationCard;
